import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography
} from "@mui/material";
import { FilterList, GridViewOutlined, UnfoldLess, UnfoldMore, ViewTimelineOutlined } from "@mui/icons-material";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { PaginationControls, SearchField, DrawerControl, FilterSideBar } from "../../../shared/components";

import { useNavigation } from "../../../hooks/useNavigation";

import { ProjectCard, useFetchProjects } from "../index";
import { Phase } from "../types";
import { ProjectsGanttView, ZoomLevel } from "./ProjectsGanttView";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useActiveFilterLabels } from "../../../shared/hooks";
import { clientTheme } from "../../../theme/clientTheme";

const projectCardVariant = clientTheme.layout?.projectCardVariant ?? "classic";



export const AllProjects: React.FC<{ isPinned: boolean, isCompleted: boolean }> = ({ isPinned, isCompleted }) => {

  const { isAllowedToAddProject } = useCapabilities();
  const { portfolioId, portfolioName } = useParams();
 
  const [assignedToMe, setAssignedToMe] = useState(false);

  const {
    isLoading, isError, projects, pagination, searchDraft, projectStages, status, isOpenFilter,
    setStatus, handleChangeSearch, handleClear, handleSearch, changePage, changeRowsPerPage,
    handleOpenFilter, handleCloseFilter, handlePinProject
  } = useFetchProjects(isPinned, isCompleted, portfolioId, { my_tasks: assignedToMe });

  const ganttRef = useRef<{
    expandAll: () => void;
    collapseAll: () => void;
  } | null>(null);

  const { goToProjectDetails, goToProjectDetailsFromPortfolio } = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasActiveFilters, activeFilterLabels} = useActiveFilterLabels();

  const viewParam = searchParams.get("view");
  const [alignment, setAlignment] = useState<string | null>(() => {
    return viewParam === "gantt" ? "center" : "left";
   });
 const [zoomLevel, setZoomLevel] = useState<ZoomLevel>("month");
 const [isCollapsed, setIsCollapsed] = useState(false);

 const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

  useEffect(() => {
    const currentView = viewParam === "gantt" ? "center" : "left";
    if (alignment !== currentView) {
      setAlignment(currentView);
    }
  }, [viewParam]);

  const handleAlignment = (
    _e: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment === null) return;
    setAlignment(newAlignment);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (newAlignment === "center") {
        newParams.set("view", "gantt");
      } else {
        newParams.delete("view");
      }
      return newParams;
    });
  };

  const handleZoomChange = (
    _e: React.MouseEvent<HTMLElement>,
    newZoom: ZoomLevel | null
  ) => {
    if (newZoom) setZoomLevel(newZoom);
  };

  const handleToggleCollapse = () => {
    if (isCollapsed) {
      ganttRef.current?.expandAll();
    } else {
      ganttRef.current?.collapseAll();
    }
    setIsCollapsed(!isCollapsed);
  };

  const handleNavigate = (project: { id: string | number, title: string }) => {
    if (portfolioId && portfolioName && portfolioId) {
      goToProjectDetailsFromPortfolio(portfolioName, portfolioId, String(project.id), String(project.title));
    } else {
      goToProjectDetails(Number(project.id), String(project.title));
    }
  };

  if (isLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Alert
          severity="warning"
          sx={{
            width: "auto",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {isError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={1.5}>

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} px={2}>

        <Box display="flex" alignItems="center" gap={2}>
          <ToggleButtonGroup
          value={alignment}
          exclusive
          onChange={handleAlignment}
          aria-label="view toggle"
          size="small"
        >
          <ToggleButton value="left" aria-label="grid view">
            <GridViewOutlined sx={{ color: "text.secondary" }} />
          </ToggleButton>
          <ToggleButton value="center" aria-label="list view">
            <ViewTimelineOutlined sx={{ color: "text.secondary" }} />
          </ToggleButton>
        </ToggleButtonGroup>

        <SearchField
          label="Project Reference, Title or Address"
          searchTerm={searchDraft}
          handleSearch={handleChangeSearch}
          handleClearSearch={handleClear}
          handleApplySearch={handleSearch}
        />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
              {projectCardVariant === "modern" ? (
                <FormControlLabel
                  control={<Checkbox checked={assignedToMe} onChange={handleAssignedToMeChange} size="small" />}
                  label={<Typography variant="body2">Assigned to Me</Typography>}
                  sx={{ m: 0 }}
                />
              ) : (
                <>
                  <Typography variant="subtitle2">Assigned to Me: </Typography>
                  <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                </>
              )}
            </Box>
          <Tooltip
            title={
              hasActiveFilters ? (
                <Box sx={{ p: 0.5 }}>
                  {activeFilterLabels.map((label, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {label}
                    </Typography>
                  ))}
                </Box>
              ) : ""
            }
            placement="bottom-start"
            disableHoverListener={!hasActiveFilters}
            arrow
          >
            <Button
              startIcon={<FilterList />}
              variant={projectCardVariant === "modern" ? "outlined" : (hasActiveFilters ? "contained" : "text")}
              color={projectCardVariant === "modern" ? "inherit" : (hasActiveFilters ? undefined : "inherit")}
              onClick={handleOpenFilter}
              sx={
                projectCardVariant !=="modern" && hasActiveFilters
                  ? {
                    backgroundColor: "action.selected",
                    color: "text.primary",
                    "&:hover": { backgroundColor: "action.hover" },
                  }
                  : projectCardVariant === "modern"
                  ? { borderRadius: 2, textTransform: "none", borderColor: "divider" }
                  : undefined
              }
            >
              {projectCardVariant === "modern" ? "Filters" : `FILTERS${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}`}
              {projectCardVariant === "modern" && hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}
            </Button>
          </Tooltip>
        </Box>

        {alignment === "center" && ( <Box display="flex" alignItems="center" gap={2}>
          <Button
          variant="text"
          size="small"
          startIcon={isCollapsed ? <UnfoldMore /> : <UnfoldLess />}
          onClick={handleToggleCollapse}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.8125rem",
          }}
        >
          {isCollapsed ? "Expand All" : "Collapse All"}
        </Button>
          <ToggleButtonGroup
            value={zoomLevel}
            exclusive
            onChange={handleZoomChange}
            size="small"
            sx={{ ml: "auto" }}
          >
            <ToggleButton value={"day" as ZoomLevel}>Day</ToggleButton>
            <ToggleButton value={"week" as ZoomLevel}>Week</ToggleButton>
            <ToggleButton value={"month" as ZoomLevel}>Month</ToggleButton>
            <ToggleButton value={"year" as ZoomLevel}>Year</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        )}

      </Box>

      <Box display="flex" gap={1} flexWrap="wrap" px={2}>
        {[...projectStages].sort((a, b) => a.sequence_order - b.sequence_order).map((stage, index) => {
          const isActive = stage.id === status.id && stage.name === status.name;
          return projectCardVariant === "modern" ? (
            <Chip
              key={index}
              disabled={isLoading}
              label={stage.name}
              variant={isActive ? "filled" : "outlined"}
              color={isActive ? "primary" : "default"}
              onClick={() => { changePage(1); setStatus({ id: stage.id, name: stage.name }); }}
              sx={{ borderRadius: "999px", fontWeight: isActive ? 600 : 400 }}
            />
          ) : (
            <Chip
              key={index}
              disabled={isLoading}
              variant="filled"
              color={isActive ? "primary" : "default"}
              label={`${stage.name} (${stage.project_count})`}
              onClick={() => { changePage(1); setStatus({ id: stage.id, name: stage.name }); }}
            />
          );
        })}
      </Box>

      <Box flex={1} overflow="auto" px={alignment === "center" ? 0 : 2} pb={alignment === "center" ? 0 : 2}>
        {projects.length === 0 ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Alert
              severity="warning"
              sx={{
                width: "auto",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {isAllowedToAddProject ? "No projects found. You can add a new project to get started." : "No projects found."}
            </Alert>
          </Box>
        ) : alignment === "center" ? (
          <ProjectsGanttView
            ref={ganttRef}
            projects={projects}
            zoomLevel={zoomLevel}
            onProjectClick={(projectId: number, projectName: string) => { goToProjectDetails(projectId, projectName) }}
          />
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="start"
            alignItems="stretch"
            alignContent="stretch"
          >
            {projects.map((project: { title: string; address: string; workstream: string; work_type_description: string; phase_name: string; project_reference: string; phases: Phase[]; progress: { percentage: number; }; id: string | number; rag: { time: "green" | "red" | "amber"; cost: "green" | "red" | "amber"; quality: "green" | "red" | "amber"; overall: "green" | "red" | "amber"; }; is_fav: boolean; planned_start_date?: string | null; planned_end_date?: string | null; }, index: React.Key | null | undefined) => (
              <Grid key={index} size={{ sm: 12, xs: 12, md: 3, lg: 3 }}>
                <ProjectCard
                  title={project.title}
                  location={project.address}
                  workType={project.workstream}
                  workTypeDescription={project.work_type_description}
                  approvalStage={project.phase_name}
                  projectId={project.project_reference}
                  phases={project.phases}
                  progress={project.progress.percentage}
                  handleClick={() => handleNavigate(project)}
                  ragStatus={project.rag}
                  isPinned={project.is_fav}
                  handlePinToggle={async (pin: boolean) => {
                    await handlePinProject(String(project.id), pin);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <PaginationControls
        total={pagination.total}
        rowsPerPage={pagination.limit}
        currentPage={pagination.page}
        handlePageChange={changePage}
        handleRowsPerPage={changeRowsPerPage}
      />

      <DrawerControl
        title="Filter Records"
        subTitle="Please apply following filters to refine results"
        isOpen={isOpenFilter}
        handleClose={handleCloseFilter}
      >
        <FilterSideBar onClose={handleCloseFilter} />
      </DrawerControl>

    </Box>
  );
};
